import styles from "./common.module.css"
import React from "react"
import cx from "classnames"
import * as finance from "shared/finance"
import * as date from "shared/date"
import * as recharts from "recharts"

export const colors = ["#006AF9", "#64D254", "#FFBB00", "#C4A8DF", "#FF4444", "#65DFC5", "#FF91B0", "#005F8B", "#A7DD00", "#FF4444"]

/* Prepands a list of values with months */
export const prependMonths = (schedule) => schedule.map((vs, idx) => [idx + 1].concat(vs))

/*
 * Component selectively converts balances intended for a graph
 * from monthly time periods to years. values takes the shape of
 * [
 *   [1, 2322, ...],
 *   [2, 422, ...],
 *   ...
 * ] with the first item in
 * each tuple representing the month.
 */
export const Balances = ({render, values = [], maxMonths = 36, tooltip = ["Date", {name: "Balance", type: "currency", color: "#006AF9"}], colors, ariaLabel}) => {
  let xAxis
  let vs
  if (values.length > maxMonths) {
    xAxis = "Years"
    // Convert months to years
    const year = new Date().getFullYear()
    vs = values.reduce((memo, [period, ...vs]) => {
      let last = memo[memo.length - 1]
      if (period % 12 === 0) {
        memo.push([last[0] + 1].concat(vs)) // Breakpoint
      } else {
        if (last && finance.sum(vs) === 0) {
          memo.push([last[0] + 1].concat(vs)) // Last iteration, zero balance
        } else if (last) {
          last = [last[0]].concat(vs) // Middle iterations
        } else {
          memo.push([year].concat(vs)) // First iteration
        }
      }
      return memo
    }, [])
  } else {
    xAxis = "Months"
    const today = date.today()
    vs = values.map(([period, ...vs], idx) => [date.shortMonthYear(date.monthsFromDate(today, idx))].concat(vs))
  }
  return render({
    values: vs,
    colors,
    ariaLabel,
    tooltip,
    xAxis,
    yAxis: "Balances",
    formatter: "currency",
    isStacked: true
  })
}

const formatters = {
  [null]: (v) => v,
  currency: (v) => finance.asCurrency(v)
}

const format = (formatter, v) => (v ? formatters[formatter](v) : "N/A")

const stringProperty = (p, idx, payload) => ({
  name: p,
  value: idx === 0 ? payload.name : payload[idx - 1],
  type: null,
  color: idx === 0 ? null : colors[idx - 1]
})

const objectProperty = ({name, type = null, color = null, idx}, index, payload) => ({
  name,
  value: idx ? payload[idx - 1] : index === 0 ? payload.name : payload[index - 1],
  type,
  color: color || (index === 0 ? null : colors[index - 1])
})

const tooltipProperties = (tooltip, payload) =>
  tooltip.map((property, index) => {
    const f = typeof property === "string" ? stringProperty : objectProperty
    return f(property, index, payload)
  })

/*
 * Unfortunately, Recharts requires the <recharts.Tooltip>
 * element to be directly beneath <recharts.LineChart>
 * preventing better composability. Settling for sharing
 * props.
 */

const TooltipProperty = ({data, property, index, theme}) => {
  const tooltip = data.fields[property.id].tooltip
  return (
    <div className={cx(styles["tooltip"], fonts.getFontClass())}>
      <span className={styles["with-indicator"]}>
        {tooltip.color && <div style={{background: "#" + theme[tooltip.color]}} className={styles["indicator"]} />}
        {tooltip.title}:
      </span>
      <span className={styles["value"]}>{formatTooltipValue(property.value, property.type)}</span>
    </div>
  )
}

const Tooltip = ({idx, name, type, value, color}) => (
  <div key={idx} className={styles["node"]}>
    <span className={styles["with-indicator"]}>
      {color && <div style={{background: color}} className={styles["indicator"]} />}
      <span className={styles["label"]}>{name}:</span>
    </span>
    <span className={styles["value"]}>{format(type, value)}</span>
  </div>
)

const TooltipNoLabel = ({idx, value, color}) => (
  <div key={idx} className={styles["node"]}>
    <span className={styles["label"]} style={{color: color}}>
      {value}
    </span>
  </div>
)

const tooltipHeights = {
  2: 42,
  3: 66,
  4: 90
}

const tooltipStyles = ({width = 180, rows = 3}) => ({
  background: "white",
  borderRadius: 4,
  boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.30), 0px 2px 6px 2px rgba(0, 0, 0, 0.15)",
  padding: 12,
  width: width,
  height: tooltipHeights[rows] || tooltipHeights[3],
  textAlign: "left",
  boxSizing: "content-box",
  lineHeight: 1
})

export const tooltipProps = (styleOverrides) => {
  return {
    cursor: false,
    content: ({payload, tooltip = []}) => {
      const [obj] = payload
      if (obj) {
        const {payload} = obj
        const properties = tooltipProperties(tooltip, payload)
        return (
          <div>
            {properties.map(({name, type, value, color}, idx) =>
              !name && idx === 0 ? (
                <TooltipNoLabel key={idx} idx={idx} color={color} value={value} />
              ) : (
                <Tooltip key={idx} idx={idx} name={name} color={color} type={type} value={value} />
              )
            )}
          </div>
        )
      } else {
        return null
      }
    },
    wrapperStyle: tooltipStyles(styleOverrides)
  }
}

const legendObjectProperty = ({name, iconType = null, color = null, idx}, index, payload) => ({
  name,
  iconType,
  color
})
// okay so properties can be whatever I want, and then I'll map over it and pass it to my custom content

const legendProperties = (legend, payload) =>
  legend.map((property, index) => {
    const f = typeof property === "string" ? stringProperty : legendObjectProperty
    return f(property, index, payload)
  })

export const legendProps = ({height = 36, verticalAlign = "bottom"}) => {
  return {
    height,
    verticalAlign,
    content: ({payload, legend = []}) => {
      const [obj] = payload
      if (obj) {
        const {payload} = obj
        const properties = legendProperties(legend, payload)
        return (
          <div className={styles["legend"]}>
            {properties.map(({name, color}, idx) => (
              <div key={idx} className={styles["item"]}>
                <span className={styles["name"]}>{name}</span>
                <span
                  className={styles["dot"]}
                  style={{
                    background: color
                  }}
                ></span>
              </div>
            ))}
          </div>
        )
      } else {
        return null
      }
    },
    wrapperStyle: {
      padding: 16,
      boxSizing: "content-box",
      lineHeight: 1.2
    }
  }
}

// content is invoked by recharts, and passed the payload, which was passed as a prop to the Legend component
// then we map over it below, to render each item
